import { setCarrierDetailsChangeBaseline } from '@changeTracking/pages/carrierDetails/formChangeTracking';
import { ToastType } from '@components/Toast/Toast';
import {
  fetchCarrierLiabilityTypes,
  fetchCarrierMaxLiability,
} from '@store/ltl/services';
import {
  CarrierLiabilityTypeViewModel,
  CarrierMaxLiability,
  LTLPricingRequestType,
} from '@store/ltl/types';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { handleToast } from '@utils/utils';
import { DateTime } from 'luxon';
import { produce } from 'solid-js/store';

import {
  fetchCarrierAdjustmentAccounts,
  fetchCarrierAdjustments,
  fetchCarrierCSPCustomers,
  fetchCarrierDetails,
  fetchCarrierDocuments,
  fetchCarrierFuelSurcharge,
  fetchCarrierLoadHistory,
  fetchCSPEnabledCustomers,
  saveNewCarrierAdjustments,
} from './services';
import {
  carrierDetailsStore,
  setCarrierCSPCustomers,
  setCarrierDetailsStore,
  setCarrierLoadHistoryStore,
} from './store';
import {
  CarrierCSPAccount,
  CarrierDetails,
  CarrierLoadHistory,
  CSPEnabledCustomerLookup,
  LoadHistoryPayload,
  MaxLiabilityCustomer,
} from './types';

export const getCarrierDetails = async (carrierId: string) => {
  setCarrierDetailsStore('isLoading', true);
  const response = await fetchCarrierDetails(carrierId);

  //sort the insurances by insurance type
  response?.carrierInsurances.sort((a, b) => {
    if (a.insuranceType === b.insuranceType) {
      return (a.internalStatus ?? '').localeCompare(b.internalStatus ?? '');
    }

    return a.insuranceType.localeCompare(b.insuranceType);
  });

  //set the internal status of the insurance
  response?.carrierInsurances.forEach((insurance) => {
    if (
      DateTime.fromISO(insurance.expirationDate ?? '') <
      DateTime.now().startOf('day')
    ) {
      insurance.internalStatus = 'Expired';
    } else if (
      DateTime.fromISO(insurance.effectiveDate ?? '') >
      DateTime.now().startOf('day')
    ) {
      insurance.internalStatus = 'Pending Active';
    } else {
      insurance.internalStatus = 'Active';
    }
  });

  if (response) {
    setCarrierDetailsStore(
      produce((draft) => {
        draft.carrierDetails = response;
        draft.isLoading = false;
        draft.isError = false;
        draft.selectedCountry = (() => {
          if (response.usaAddress.enabled) {
            return 'US';
          } else if (response.canAddress.enabled) {
            return 'CA';
          } else if (response.mexAddress.enabled) {
            return 'MX';
          }

          return 'US';
        })();

        draft.showDuplicateBanner = Boolean(response.isDuplicate);
      }),
    );

    setCarrierDetailsChangeBaseline(response);
  } else {
    setCarrierDetailsStore('isLoading', false);
  }
};

export const getCarrierAdjustments = async (carrierId: string) => {
  const response = await fetchCarrierAdjustments(carrierId);
  if (response) {
    setCarrierDetailsStore('carrierAdjustments', response.value);
  }
};

export const getCarrierAdjustmentAccounts = async () => {
  const response = await fetchCarrierAdjustmentAccounts();
  if (response) {
    setCarrierDetailsStore('carrierAdjustmentAccounts', response);
  }
};

export const getCarrierDocuments = async (carrierId: string) => {
  const response = await fetchCarrierDocuments(carrierId);
  if (Boolean(response)) setCarrierDetailsStore('carrierDocuments', response!);
};

export const getCarrierFuelSurcharge = async (carrierId: string) => {
  const response = await fetchCarrierFuelSurcharge(carrierId);
  if (Boolean(response))
    setCarrierDetailsStore('carrierFuelSurcharge', response!);
};

export const updateCarrierDetailsState = (updates: Partial<CarrierDetails>) => {
  Object.entries(updates).forEach(([key, value]) => {
    setCarrierDetailsStore(
      'carrierDetails',
      key as keyof CarrierDetails,
      value,
    );
  });

  if (carrierDetailsStore.carrierDetails?.operationType === 'None') {
    setCarrierDetailsStore('carrierDetails', 'operationType', 'Update');
  }
};

export const getCarrierLoadHistory = async (paylaod: LoadHistoryPayload) => {
  setCarrierLoadHistoryStore('isLoading', true);
  const response = await fetchCarrierLoadHistory(paylaod);
  if (Boolean(response)) {
    setCarrierLoadHistoryStore(
      produce((draft) => {
        draft.items = response?.data as CarrierLoadHistory[];
        draft.isLoading = false;
        draft.isError = false;
        draft.totalCount = response?.totalCount as number;
      }),
    );
  } else {
    setCarrierLoadHistoryStore(
      produce((draft) => {
        draft.items = [];
        draft.isLoading = false;
        draft.isError = true;
      }),
    );
  }
};

export const saveNewCarrierDetailsAdjustment = async (
  carrierId: number,
  adjustment: Record<string, unknown>[],
  changeTrackingSource?: ChangeTrackingSource,
) => {
  const response = await saveNewCarrierAdjustments(
    carrierId,
    adjustment,
    changeTrackingSource,
  );
  if (Boolean(response)) await getCarrierAdjustments(carrierId.toString());
};

export const getCarrierCSPCustomers = async (carrierId: number) => {
  setCarrierCSPCustomers('isLoading', true);
  try {
    const resp = await fetchCarrierCSPCustomers(carrierId);
    setCarrierCSPCustomers(
      produce((draft) => {
        draft.cspCustomers = resp as CarrierCSPAccount[];
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
  } catch (error) {
    setCarrierCSPCustomers(
      produce((draft) => {
        draft.isError = true;
        draft.cspCustomers = [];
      }),
    );
  } finally {
    setCarrierCSPCustomers('isLoading', false);
  }
};

export const getCSPEnabledCustomers = async (scacId: number) => {
  setCarrierCSPCustomers('isLoading', true);
  try {
    const resp = await fetchCSPEnabledCustomers(scacId);
    setCarrierCSPCustomers(
      produce((draft) => {
        draft.cspEnabledCustomers = resp as CSPEnabledCustomerLookup[];
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
  } catch (error) {
    setCarrierCSPCustomers(
      produce((draft) => {
        draft.isError = true;
        draft.cspEnabledCustomers = [];
      }),
    );
  } finally {
    setCarrierCSPCustomers('isLoading', false);
  }
};

export const BLANKET_MAX_LIABILITY_CUSTOMER: MaxLiabilityCustomer = {
  customerId: null,
  customerName: 'Blanket',
};

export type MaxLiabilityTarget = {
  class: string;
  pricingRequestType: number;
};

export type MaxLiabilityValues = {
  maxNew?: number | null;
  maxUsed?: number | null;
};

const pricingRequestTypeFromLabel = (label: string): number => {
  const value = LTLPricingRequestType[
    label as keyof typeof LTLPricingRequestType
  ] as number | undefined;
  return value ?? LTLPricingRequestType.None;
};

// CarrierLiabilityTypeGet always includes this entry; it is never user-selectable.
const PRICING_NONSPECIFIC_LABEL = 'PricingNonspecific';

/**
 * Loads the carrier's supported pricing types, the customer scopes, and the
 * max liability rows for every supported type in the given customer scope.
 * The rows for all types land in `carrierMaxLiability`; each row carries its
 * own `pricingRequestType`, so consumers filter by type.
 */
type MaxLiabilitySetup = {
  liabilityTypes: string[];
  customerSpecific: MaxLiabilityCustomer[];
  rows: CarrierMaxLiability[];
};

/** Fetches the supported pricing types and the rows for each of them in one customer scope. */
const fetchMaxLiabilitySetup = async (
  carrierId: number,
  customerId: number | null,
): Promise<MaxLiabilitySetup> => {
  const typesResponse = (await fetchCarrierLiabilityTypes(carrierId)) as
    | CarrierLiabilityTypeViewModel
    | undefined
    | never[];
  const hasTypes = typesResponse && !Array.isArray(typesResponse);
  const liabilityTypes = hasTypes
    ? typesResponse.carrierLiabilityTypes.filter(
        (label) => label !== PRICING_NONSPECIFIC_LABEL,
      )
    : [];
  const customerSpecific = hasTypes ? typesResponse.customerSpecific : [];

  const rowsByType = await Promise.all(
    liabilityTypes.map((label) =>
      fetchCarrierMaxLiability(
        carrierId,
        pricingRequestTypeFromLabel(label),
        customerId,
      ),
    ),
  );

  return {
    liabilityTypes,
    customerSpecific: customerSpecific.map((customer) => ({
      customerId: customer.customerId,
      customerName: customer.customerName,
    })),
    rows: rowsByType.flat(),
  };
};

/**
 * Increments on every load request. A load only commits its result and
 * finalizes the loading flag when it is still the latest request, so a slow
 * response for customer A cannot overwrite a newer load for customer B.
 */
let maxLiabilityLoadGeneration = 0;

export const loadCarrierMaxLiabilitySetup = async (
  carrierId: number,
  customerId: number | null = null,
): Promise<boolean> => {
  const generation = ++maxLiabilityLoadGeneration;
  const isLatest = () => generation === maxLiabilityLoadGeneration;

  setCarrierDetailsStore('maxLiabilityLoading', true);
  try {
    const setup = await fetchMaxLiabilitySetup(carrierId, customerId);
    if (!isLatest()) return true;

    setCarrierDetailsStore(
      produce((draft) => {
        draft.carrierLiabilityTypes = setup.liabilityTypes;
        draft.maxLiabilityCustomers = [
          BLANKET_MAX_LIABILITY_CUSTOMER,
          ...setup.customerSpecific,
        ];
        draft.maxLiabilityCustomerId = customerId;
        draft.maxLiabilityCarrierId = carrierId;
        draft.carrierMaxLiability = setup.rows;
      }),
    );
    return true;
  } catch (error) {
    if (!isLatest()) return true;
    handleToast(
      ToastType.Error,
      error instanceof Error
        ? error.message
        : 'Failed to load carrier max liability',
    );
    return false;
  } finally {
    if (isLatest()) {
      setCarrierDetailsStore('maxLiabilityLoading', false);
    }
  }
};

/**
 * Marks rows as persisted right after a successful POST or PUT, so a later
 * retry (for example after a failed reload) does not send them again. The
 * next successful reload replaces them with the API's copies and ids.
 */
export const markMaxLiabilityRowsPersisted = (
  persisted: readonly CarrierMaxLiability[],
) => {
  if (persisted.length === 0) return;
  const keys = new Set(
    persisted.map((row) => `${row.pricingRequestType}:${row.class}`),
  );
  setCarrierDetailsStore('carrierMaxLiability', (rows) =>
    rows.map((row) =>
      keys.has(`${row.pricingRequestType}:${row.class}`)
        ? { ...row, operationType: 'None' }
        : row,
    ),
  );
};

/**
 * Writes the given values onto every target row (class + pricing type) in
 * `carrierMaxLiability`. Existing rows are merged and flagged `Update`; missing
 * rows are appended as `Insert` in the currently loaded customer scope. A value
 * left `undefined` keeps the row's current value, so a single cell edit can
 * pass only the column it changed.
 */
export const upsertMaxLiabilityRows = (
  targets: MaxLiabilityTarget[],
  values: MaxLiabilityValues,
) => {
  if (targets.length === 0) return;

  const carrierId = carrierDetailsStore.carrierDetails?.id ?? 0;
  const customerId = carrierDetailsStore.maxLiabilityCustomerId;

  setCarrierDetailsStore('carrierMaxLiability', (rows) => {
    const next = [...rows];

    targets.forEach((target) => {
      const index = next.findIndex(
        (row) =>
          row.class === target.class &&
          row.pricingRequestType === target.pricingRequestType,
      );

      if (index >= 0) {
        const existing = next[index];
        next[index] = {
          ...existing,
          maxNew: values.maxNew === undefined ? existing.maxNew : values.maxNew,
          maxUsed:
            values.maxUsed === undefined ? existing.maxUsed : values.maxUsed,
          operationType: existing.id > 0 ? 'Update' : 'Insert',
        };
        return;
      }

      const inserted: CarrierMaxLiability = {
        id: 0,
        carrierId,
        customerId,
        pricingRequestType: target.pricingRequestType,
        class: target.class,
        maxNew: values.maxNew ?? null,
        maxUsed: values.maxUsed ?? null,
        operationType: 'Insert',
      };
      next.push(inserted);
    });

    return next;
  });
};
